import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Sparkles, Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";
import { DefineAiDisclosure } from "./DefineAiDisclosure";
import { useState } from "react";

export function SmartGoalTab({
  projectId,
  token,
  smartGoal,
  setSmartGoal,
  saving,
  aiGenerating,
  hasProblemText,
  measurableWarning,
  aiDraftActive,
  savedNotice,
  saveSmartGoal,
  generateSmartGoal,
  continueToSipoc,
}) {
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const sectionHeaderClassName = "flex flex-wrap items-start justify-between gap-3 border-b border-border/60 pb-4";
  const sectionActionClassName = "flex flex-wrap items-center gap-2 self-start";

  return (
    <div className="bg-card border border-border rounded-lg p-5 space-y-4">
      <div className={sectionHeaderClassName}>
        <h3 className="font-heading text-xl font-semibold leading-none">SMART Goal</h3>
        <div className={sectionActionClassName}>
          {savedNotice && (
            <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-300" data-testid="smart-saved-notice">
              Saved
            </span>
          )}
          <Button onClick={saveSmartGoal} disabled={saving} className="min-w-[148px]" data-testid="save-smart-btn">
            {saving ? "Saving..." : "Save SMART Goal"}
          </Button>
          <Button
            variant="outline"
            onClick={generateSmartGoal}
            disabled={!!aiGenerating || !hasProblemText}
            className="gap-2 min-w-[148px]"
            data-testid="ai-generate-smart-btn"
          >
            {aiGenerating === "SMART Goal" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            AI Generate Goal
            <CreditCostTag cost={2} />
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {aiDraftActive && (
          <DefineAiDisclosure
            title="AI-generated SMART draft"
            detail="This SMART Goal was generated from Define context. Measurable language may still include assumptions or unvalidated placeholders until Measure confirms the baseline."
            testId="smart-goal-ai-disclosure"
          />
        )}
        <div className="rounded-lg border border-border/70 bg-background/25" data-testid="smart-goal-guidance">
          <button
            type="button"
            onClick={() => setGuidanceOpen((current) => !current)}
            className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-background/30"
          >
            <span className="text-sm font-medium text-muted-foreground">Guidance</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${guidanceOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`overflow-hidden transition-all duration-200 ease-out ${guidanceOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
            <div className="border-t border-border/60 px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Keep SMART structure intact, but let Measurable start with baseline establishment and capability validation when the current process performance has not yet been confirmed.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Avoid inventing numeric targets in Define. If you intentionally want to record a provisional target, make it clear that Measure must validate the baseline first.
            </p>
            </div>
          </div>
        </div>

        <div className="space-y-2 border-b border-border/60 pb-3">
          <Label>Specific - What exactly will you accomplish?</Label>
          <Textarea
            placeholder="Reduce processing time for customer orders..."
            value={smartGoal.specific}
            onChange={(e) => setSmartGoal((prev) => ({ ...prev, specific: e.target.value }))}
            rows={2}
            autoResize
            maxHeight={320}
            className="max-w-5xl leading-7 font-normal"
            data-testid="smart-specific-input"
          />
        </div>

        <div className="space-y-2 border-b border-border/60 pb-3">
          <Label>Measurable - How will you measure success?</Label>
          <Textarea
            placeholder="Establish the current baseline for turnaround time, validate process capability, then set the final target after Measure confirms the baseline..."
            value={smartGoal.measurable}
            onChange={(e) => setSmartGoal((prev) => ({ ...prev, measurable: e.target.value }))}
            rows={2}
            autoResize
            maxHeight={320}
            className="max-w-5xl leading-7 font-normal"
            data-testid="smart-measurable-input"
          />
          <p className="text-xs text-muted-foreground">
            Default in Define: name the metric, baseline approach, and validation path first. Only use a quantified target here when you explicitly have or approve one.
          </p>
          {measurableWarning && (
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              data-testid="smart-measurable-warning"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p>{measurableWarning.message}</p>
            </div>
          )}
        </div>

        <div className="space-y-2 border-b border-border/60 pb-3">
          <Label>Achievable - Is this realistic?</Label>
          <Textarea
            placeholder="Based on benchmark data and available resources..."
            value={smartGoal.achievable}
            onChange={(e) => setSmartGoal((prev) => ({ ...prev, achievable: e.target.value }))}
            rows={2}
            autoResize
            maxHeight={320}
            className="max-w-5xl leading-7 font-normal"
            data-testid="smart-achievable-input"
          />
        </div>

        <div className="space-y-2 border-b border-border/60 pb-3">
          <Label>Relevant - Why does this matter?</Label>
          <Textarea
            placeholder="Aligns with strategic goal of improving customer satisfaction..."
            value={smartGoal.relevant}
            onChange={(e) => setSmartGoal((prev) => ({ ...prev, relevant: e.target.value }))}
            rows={2}
            autoResize
            maxHeight={320}
            className="max-w-5xl leading-7 font-normal"
            data-testid="smart-relevant-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Time-bound - When will this be achieved?</Label>
          <Textarea
            placeholder="Within 6 months from project start..."
            value={smartGoal.time_bound}
            onChange={(e) => setSmartGoal((prev) => ({ ...prev, time_bound: e.target.value }))}
            rows={2}
            autoResize
            maxHeight={320}
            className="max-w-5xl leading-7 font-normal"
            data-testid="smart-timebound-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={continueToSipoc} className="min-w-[164px]" data-testid="continue-to-sipoc-btn">
            Continue to SIPOC
          </Button>
          <ArtifactQualityReview
            projectId={projectId}
            artifactType="smart_goal"
            token={token}
          />
        </div>
      </div>
    </div>
  );
}
